import { useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useDropzone } from 'react-dropzone';
import {
  Upload,
  FileSpreadsheet,
  Users,
  TrendingUp,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Download,
  Eye,
  History,
  RefreshCcw,
  ChevronRight,
  FileWarning,
  Clock,
  Check,
  X,
  Loader2,
  Wallet,
  BarChart3,
  Database,
  HelpCircle,
  Info,
  Calendar as CalendarIcon,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';

// ============================================================================
// DEBUG MODE ENABLED - P0 CLIENT MASTER IMPORT INVESTIGATION
// ============================================================================
const DEBUG = true;

const debugLog = (stage, data) => {
  if (DEBUG) {
    const timestamp = new Date().toISOString();
    console.log(`%c[IMPORT DEBUG ${timestamp}] ${stage}`, 'background: #ff6600; color: white; padding: 2px 6px; border-radius: 3px;', data);
  }
};

// Smart API URL detection - use relative for external preview, env var for localhost
const getApiUrl = () => {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : 'unknown';
  const isPreview = hostname.includes('preview.emergentagent.com');
  const envUrl = process.env.REACT_APP_BACKEND_URL || '';
  
  debugLog('API_URL_DETECTION', {
    hostname,
    isPreview,
    envUrl,
    result: isPreview ? '' : envUrl
  });
  
  // If accessed via preview URL, use relative paths (ingress handles routing)
  if (isPreview) {
    return '';
  }
  // For localhost development, use the backend URL from env
  return envUrl;
};
const API_URL = getApiUrl();
debugLog('API_URL_INITIALIZED', { API_URL, fullPreviewUrl: `${API_URL}/api/import/preview` });

// Maximum file size: 50 MB
const MAX_FILE_SIZE = 50 * 1024 * 1024;

// Import type configurations (P0 UPDATED)
const IMPORT_TYPES = {
  clients: {
    label: 'Client Master',
    icon: Users,
    description: 'Import client master data (P0: Source of Truth)',
    endpoint: '/api/import/clients',
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10',
    // P0: Updated required/optional columns per user requirements
    requiredColumns: ['client_code', 'name'],
    optionalColumns: ['email', 'phone', 'pan', 'status', 'segment', 'kyc_status', 'address', 'city', 'state', 'assigned_dealer_id', 'assigned_rm_id'],
    columnHelp: {
      client_code: {
        label: 'Client Code',
        description: 'Unique identifier for the client. This is the PRIMARY KEY used across all modules.',
        example: 'C00001, CLI001, etc.',
        values: [
          { value: 'Required', desc: 'Must be unique across the system' },
          { value: 'Immutable', desc: 'Cannot be changed after creation' },
        ]
      },
      name: {
        label: 'Client Name',
        description: 'Full name of the client for display purposes',
        example: 'John Doe'
      },
      status: {
        label: 'Status',
        description: 'Client status - INACTIVE clients are blocked from transactional use',
        example: 'Active or Inactive',
        values: [
          { value: 'Active', desc: 'Client can be used in brokerage/portfolio uploads' },
          { value: 'Inactive', desc: 'Blocked from new brokerage, portfolio uploads, and dealer assignments' },
        ]
      },
      pan: {
        label: 'PAN Number',
        description: 'Permanent Account Number (optional, validated format)',
        example: 'ABCDE1234F'
      },
      assigned_dealer_id: {
        label: 'Assigned Dealer',
        description: 'Dealer/RM ID to assign this client to',
        example: 'DLR001 or dealer UUID'
      },
      assigned_rm_id: {
        label: 'Assigned RM',
        description: 'Relationship Manager ID (if different from dealer)',
        example: 'RM001 or RM UUID'
      }
    }
  },
  brokerage: {
    label: 'Brokerage Data',
    icon: Wallet,
    description: 'Import daily brokerage records (YTD cumulative format)',
    endpoint: '/api/import/brokerage',
    color: 'text-[#C5A059]',
    bgColor: 'bg-[#C5A059]/10',
    requiresDate: true,  // NEW: User must select brokerage date
    requiredColumns: ['client_code', 'brokerage_ytd'],
    optionalColumns: ['segment'],
    fileFormat: {
      description: 'File Structure: Row 1-2 ignored, Row 3 = Headers, Data from Row 4',
      columns: ['IntroCode', 'BranchID', 'SegmentID', 'ClientCode', 'ClientName', 'TotalBrok', 'IntroBrok', '...']
    },
    columnHelp: {
      client_code: {
        label: 'Client Code (ClientCode)',
        description: 'Must reference an existing client in the system.',
        example: 'C00001'
      },
      brokerage_ytd: {
        label: 'YTD Brokerage (IntroBrok)',
        description: 'Cumulative YTD brokerage amount. System computes daily brokerage automatically.',
        example: '150000'
      },
      segment: {
        label: 'Segment (SegmentID)',
        description: 'Optional. CAP for Equity, FNO for Derivatives.',
        example: 'CAP, FNO'
      }
    }
  },
  portfolio: {
    label: 'Portfolio Holdings (AUM)',
    icon: BarChart3,
    description: 'Import client portfolio positions - supports flexible column formats',
    endpoint: '/api/import/portfolio',
    color: 'text-violet-500',
    bgColor: 'bg-violet-500/10',
    requiredColumns: ['client_code'],
    optionalColumns: ['security_name', 'isin', 'symbol', 'quantity', 'close_price', 'current_price', 'holding_value', 'buy_avg_price', 'notional_pnl', 'as_of_date', 'valuation_date'],
    disclaimer: 'Flexible import: Accepts both Portfolio and AUM Equity file formats. Empty quantity/price values default to 0.',
    columnHelp: {
      client_code: {
        label: 'Client Code',
        description: 'Must reference an existing client in the system.',
        example: 'C00001'
      },
      security_name: {
        label: 'Security Name',
        description: 'Name of the security/stock. At least one of security_name, isin, or symbol is required.',
        example: 'RELIANCE INDUSTRIES LTD'
      },
      quantity: {
        label: 'Quantity',
        description: 'Number of shares/units held. Defaults to 0 if empty.',
        example: '100'
      },
      close_price: {
        label: 'Close Price / Current Price',
        description: 'Current market price. Also accepts CURRENT_PRICE, LTP, CMP.',
        example: '2450.50'
      },
      holding_value: {
        label: 'Holding Value',
        description: 'Total market value. Auto-calculated as quantity × price if missing.',
        example: '245050'
      }
    }
  },
  aum: {
    label: 'AUM Records',
    icon: Database,
    description: 'Import AUM snapshots for clients, dealers, or firm',
    endpoint: '/api/import/aum',
    color: 'text-rose-500',
    bgColor: 'bg-rose-500/10',
    requiredColumns: ['scope_code', 'scope_type', 'date', 'total_aum'],
    optionalColumns: ['aum_equity', 'aum_mutual_fund', 'aum_derivatives'],
    columnHelp: {
      scope_type: {
        label: 'Scope Type',
        description: 'The level/entity type for which AUM is recorded',
        values: [
          { value: 'client', desc: 'AUM for an individual client' },
          { value: 'dealer', desc: 'Aggregated AUM for a dealer/RM (all their clients combined)' },
          { value: 'firm', desc: 'Total AUM for the entire firm' },
        ],
        example: 'client, dealer, or firm'
      },
      scope_code: {
        label: 'Scope Code',
        description: 'The unique identifier corresponding to the scope_type',
        values: [
          { value: 'For client', desc: 'Client Code (e.g., C00001)' },
          { value: 'For dealer', desc: 'Dealer Code (e.g., DLR001)' },
          { value: 'For firm', desc: 'Firm identifier (e.g., ISPRAVA)' },
        ],
        example: 'C00001 (client), DLR001 (dealer), ISPRAVA (firm)'
      },
      date: {
        label: 'Date',
        description: 'The date for this AUM snapshot',
        example: '2025-12-25 (YYYY-MM-DD format)'
      },
      total_aum: {
        label: 'Total AUM',
        description: 'Total Assets Under Management value',
        example: '2500000 (numeric, no currency symbols)'
      }
    }
  },
  // P0: AUM Equity Import - SNAPSHOT REPLACE mode
  aum_equity: {
    label: 'AUM (Equity)',
    icon: TrendingUp,
    description: 'Import Equity holdings - REPLACES existing data for the date',
    endpoint: '/api/import/aum-equity',
    color: 'text-emerald-500',
    bgColor: 'bg-emerald-500/10',
    autoDetectDate: true,  // Auto-detect date from Row 1 or Row 2
    requiresDateFallback: true,  // Show date picker if auto-detection fails
    fileFormat: {
      description: 'Row 1 or 2: Header with date (e.g., "ClientHolding as on 31/12/2025"), Row 3-5: Ignored, Row 6: Column headers, Row 7+: Data',
      dateExtraction: 'Auto-detects date from Row 1 or Row 2 (DD/MM/YYYY, DD-MM-YYYY, YYYY-MM-DD, DD Mon YYYY)'
    },
    requiredColumns: ['client_code', 'security_name'],
    optionalColumns: ['client_name', 'total_holding', 'close_rate', 'holding_value', 'buy_avg', 'notional_pnl', 'isin'],
    disclaimer: '⚠️ SNAPSHOT MODE: Importing will REPLACE all existing AUM data. Date is auto-extracted from Row 1 or Row 2.',
    columnHelp: {
      client_code: {
        label: 'Client Code',
        description: 'Primary identifier - must exist in Client Master',
        example: 'C00001',
        values: [
          { value: 'Required', desc: 'Must match an existing client' },
        ]
      },
      as_of_date: {
        label: 'As Of Date (Auto-Detected)',
        description: 'Extracted from Row 1 header text. Example: "ClientHolding as on 31/12/2025" → 2025-12-31',
        example: 'Auto-detected or manually provided if not found'
      },
      security_name: {
        label: 'Security Name',
        description: 'Name of the equity security/stock',
        example: 'RELIANCE, TCS, INFY'
      },
      isin: {
        label: 'ISIN',
        description: 'International Securities Identification Number (alternative to security_name)',
        example: 'INE002A01018'
      },
      total_holding: {
        label: 'Total Holding / Quantity',
        description: 'Number of shares held',
        example: '100 (numeric, defaults to 0)'
      },
      close_rate: {
        label: 'Close Rate / Close Price',
        description: 'Current market price / last traded price',
        example: '2500.50 (numeric)'
      },
      holding_value: {
        label: 'Holding Value',
        description: 'Total market value of the holding',
        example: '250050 (numeric)'
      },
      buy_avg: {
        label: 'Buy Average',
        description: 'Average purchase price per share',
        example: '2300.00 (numeric)'
      },
      notional_pnl: {
        label: 'Notional P&L',
        description: 'Unrealized profit/loss',
        example: '20050 or -5000 (numeric, can be negative)'
      }
    }
  },
  // NEW: Ledger Balance Import - Daily snapshot
  ledger_balance: {
    label: 'Ledger Balance',
    icon: Wallet,
    description: 'Import daily ledger balance snapshot',
    endpoint: '/api/import/execute',
    color: 'text-purple-500',
    bgColor: 'bg-purple-500/10',
    requiredColumns: ['account_id'],
    optionalColumns: ['account_name', 'net_debit', 'net_credit'],
    disclaimer: 'Ledger balance is a daily snapshot. Each upload overwrites previous values.',
    columnHelp: {
      account_id: {
        label: 'Account ID',
        description: 'Client code - must exist in Client Master',
        example: '1501, C00001',
        values: [
          { value: 'Required', desc: 'Maps to client_code in Client Master' },
        ]
      },
      account_name: {
        label: 'Account Name',
        description: 'Client name (ignored - using name from Client Master)',
        example: 'John Doe'
      },
      net_debit: {
        label: 'Net Debit',
        description: 'If value > 0, ledger balance becomes negative',
        example: '12500 → ledger_balance = -12500'
      },
      net_credit: {
        label: 'Net Credit',
        description: 'If value > 0, ledger balance becomes positive',
        example: '5000 → ledger_balance = +5000'
      }
    }
  },
};

// Status badge component
const StatusBadge = ({ status }) => {
  const statusConfig = {
    pending: { color: 'bg-slate-600', label: 'Pending' },
    validating: { color: 'bg-blue-600', label: 'Validating' },
    processing: { color: 'bg-amber-600', label: 'Processing' },
    completed: { color: 'bg-emerald-600', label: 'Completed' },
    failed: { color: 'bg-red-600', label: 'Failed' },
    partial: { color: 'bg-orange-600', label: 'Partial' },
  };
  const config = statusConfig[status] || { color: 'bg-slate-600', label: status };
  return <Badge className={`${config.color} text-white`}>{config.label}</Badge>;
};

const ImportPage = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('upload');
  const [importType, setImportType] = useState('clients');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [previewData, setPreviewData] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importHistory, setImportHistory] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  // CRITICAL: Schema Control State
  const [schemaCheck, setSchemaCheck] = useState(null);
  const [showSchemaWarning, setShowSchemaWarning] = useState(false);
  const [ignoreUnknownColumns, setIgnoreUnknownColumns] = useState(false);
  
  // Brokerage date (user-selected, required for brokerage imports)
  const [brokerageDate, setBrokerageDate] = useState(new Date().toISOString().split('T')[0]);
  
  // AUM date (auto-detected or user fallback)
  const [aumDate, setAumDate] = useState(new Date().toISOString().split('T')[0]);
  const [showAumDatePicker, setShowAumDatePicker] = useState(false);

  // Rollback feature state
  const [rollbackDataType, setRollbackDataType] = useState('brokerage');
  const [rollbackDate, setRollbackDate] = useState('');
  const [rollbackPreview, setRollbackPreview] = useState(null);
  const [rollbackLoading, setRollbackLoading] = useState(false);
  const [rollbackDateRanges, setRollbackDateRanges] = useState(null);
  const [showRollbackConfirm, setShowRollbackConfirm] = useState(false);
  const [recomputingAggregates, setRecomputingAggregates] = useState(false);

  // P0: Unmatched Clients State (for import enhancement feature)
  const [unmatchedClients, setUnmatchedClients] = useState([]);
  const [showUnmatchedModal, setShowUnmatchedModal] = useState(false);
  const [addingClient, setAddingClient] = useState(null);

  // ==========================================================================
  // P0 FIX: IMPORT STATE MACHINE
  // States: IDLE → VALIDATING → VALIDATED → CONFIRMING_SCHEMA → EXECUTING → COMPLETED/FAILED
  // ==========================================================================
  const [importState, setImportState] = useState('IDLE');
  const [currentBatchId, setCurrentBatchId] = useState(null);
  const executingRef = useRef(false);  // Hard lock to prevent re-entry
  
  // State machine logger
  const logStateTransition = (from, to, data = {}) => {
    const timestamp = new Date().toISOString();
    console.log(`%c[IMPORT STATE ${timestamp}] ${from} → ${to}`, 'background: #0066ff; color: white; padding: 2px 6px;', data);
  };

  // Fetch import history
  const fetchHistory = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/import/history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setImportHistory(data);
      }
    } catch (err) {
      console.error('Error fetching import history:', err);
    }
  }, [token]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  // Rollback: Fetch date ranges when data type changes
  const fetchRollbackDateRanges = useCallback(async (dataType) => {
    try {
      setRollbackLoading(true);
      const res = await fetch(`${API_URL}/api/import/rollback/date-ranges?data_type=${dataType}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setRollbackDateRanges(data);
      } else {
        setRollbackDateRanges(null);
      }
    } catch (err) {
      console.error('Error fetching rollback date ranges:', err);
      setRollbackDateRanges(null);
    } finally {
      setRollbackLoading(false);
    }
  }, [token]);

  // Rollback: Preview what would be deleted
  const handleRollbackPreview = async () => {
    if (!rollbackDate) {
      toast.error('Please select a rollback date');
      return;
    }
    
    try {
      setRollbackLoading(true);
      const res = await fetch(`${API_URL}/api/import/rollback/preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          data_type: rollbackDataType,
          rollback_date: rollbackDate,
          confirm: false
        }),
      });
      
      if (res.ok) {
        const data = await res.json();
        setRollbackPreview(data);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to preview rollback');
      }
    } catch (err) {
      console.error('Error previewing rollback:', err);
      toast.error('Failed to preview rollback');
    } finally {
      setRollbackLoading(false);
    }
  };

  // Rollback: Execute the rollback
  const handleRollbackExecute = async () => {
    if (!rollbackPreview) return;
    
    try {
      setRollbackLoading(true);
      const res = await fetch(`${API_URL}/api/import/rollback/execute`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          data_type: rollbackDataType,
          rollback_date: rollbackDate,
          confirm: true
        }),
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setRollbackPreview(null);
        setShowRollbackConfirm(false);
        // Refresh date ranges
        fetchRollbackDateRanges(rollbackDataType);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to execute rollback');
      }
    } catch (err) {
      console.error('Error executing rollback:', err);
      toast.error('Failed to execute rollback');
    } finally {
      setRollbackLoading(false);
    }
  };

  // Recompute brokerage aggregates manually
  const handleRecomputeAggregates = async () => {
    try {
      setRecomputingAggregates(true);
      toast.info('Recomputing aggregates... This may take up to 60 seconds for large datasets.');
      
      console.log('API_URL:', API_URL);
      const endpoint = `${API_URL}/brokerage/recompute-aggregates`;
      console.log('Calling endpoint:', endpoint);
      
      // Use AbortController for timeout handling
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 90000); // 90 second timeout
      
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      console.log('Response status:', res.status);
      
      if (res.ok) {
        // Clone response before reading to avoid "body stream already read" error
        const resClone = res.clone();
        try {
          const data = await res.json();
          toast.success(`Dashboard data refreshed! Total brokerage: ₹${(data.results?.total_brokerage || 0).toLocaleString()}`);
        } catch (parseErr) {
          // Response was OK but not JSON - still success
          console.log('Response not JSON, treating as success');
          toast.success('Dashboard data refreshed!');
        }
      } else {
        let errorMsg = 'Failed to recompute aggregates';
        try {
          const error = await res.json();
          errorMsg = error.detail || errorMsg;
          console.error('Error response:', error);
        } catch (parseErr) {
          console.error('Could not parse error response');
        }
        toast.error(errorMsg);
      }
    } catch (err) {
      console.error('Error recomputing aggregates:', err);
      if (err.name === 'AbortError') {
        toast.error('Request timed out. The operation may still be running. Please wait and refresh the dashboard in a minute.');
      } else {
        toast.error(`Network error: ${err.message}. Please try again or refresh the page.`);
      }
    } finally {
      setRecomputingAggregates(false);
    }
  };

  // Preview file - wrapped in useCallback to ensure fresh token and importType
  const handlePreview = useCallback(async (fileToPreview) => {
    debugLog('PREVIEW_START', { 
      fileName: fileToPreview?.name,
      fileSize: fileToPreview?.size,
      fileType: fileToPreview?.type,
      hasToken: !!token,
      tokenLength: token?.length,
      importType,
      API_URL
    });

    if (!fileToPreview) {
      debugLog('PREVIEW_ABORT', { reason: 'No file provided' });
      return;
    }
    
    // Verify token is available
    if (!token) {
      debugLog('PREVIEW_ABORT', { reason: 'No token available' });
      toast.error('Session expired. Please refresh the page and try again.');
      return;
    }
    
    setValidating(true);
    setProgress(0);
    setSchemaCheck(null);
    setIgnoreUnknownColumns(false);
    
    const formData = new FormData();
    formData.append('file', fileToPreview);
    formData.append('import_type', importType);
    
    const requestUrl = `${API_URL}/api/import/preview`;
    debugLog('PREVIEW_REQUEST', {
      url: requestUrl,
      method: 'POST',
      hasAuthHeader: true,
      formDataKeys: ['file', 'import_type'],
      fileDetails: {
        name: fileToPreview.name,
        size: fileToPreview.size,
        type: fileToPreview.type,
        lastModified: fileToPreview.lastModified
      }
    });
    
    try {
      // Simulate progress
      const progressInterval = setInterval(() => {
        setProgress((prev) => Math.min(prev + 10, 90));
      }, 200);
      
      debugLog('FETCH_START', { url: requestUrl, timestamp: Date.now() });
      
      const res = await fetch(requestUrl, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      
      debugLog('FETCH_COMPLETE', { 
        status: res.status,
        statusText: res.statusText,
        ok: res.ok,
        headers: Object.fromEntries(res.headers.entries()),
        url: res.url
      });
      
      clearInterval(progressInterval);
      setProgress(100);
      
      if (res.ok) {
        const data = await res.json();
        debugLog('PREVIEW_SUCCESS', { data });
        setPreviewData(data.preview);
        setValidationResult(data.validation);
        
        // STATE MACHINE: Transition to VALIDATED
        logStateTransition(importState, 'VALIDATED', { validRows: data.validation.valid_rows });
        setImportState('VALIDATED');
        
        // CRITICAL: Schema Control - Check for unknown columns
        if (data.schema_check) {
          setSchemaCheck(data.schema_check);
          if (data.schema_check.has_unknown_columns) {
            // Don't show warning yet - let user click Import button first
            toast.warning(`File contains ${data.schema_check.unknown_columns.length} unknown column(s) not in schema`);
          }
        }
        
        toast.success(`File validated: ${data.validation.valid_rows} valid, ${data.validation.error_count} errors`);
      } else {
        // P0: Enhanced error handling with specific messages
        let errorBody;
        try {
          errorBody = await res.json();
        } catch (jsonErr) {
          errorBody = { parseError: jsonErr.message, rawText: await res.text().catch(() => 'Unable to read response') };
        }
        
        debugLog('PREVIEW_HTTP_ERROR', { 
          status: res.status,
          statusText: res.statusText,
          errorBody
        });
        
        // P0 DEBUG: Show raw error body in toast for debugging
        const rawErrorStr = JSON.stringify(errorBody, null, 2);
        console.error('HTTP ERROR BODY:', rawErrorStr);
        toast.error(`HTTP ${res.status}: ${rawErrorStr.substring(0, 200)}...`);
        
        const err = errorBody;
        let errorMessage = `HTTP ${res.status}: Validation failed`;
        let errorDetails = null;
        
        if (err.detail) {
          if (typeof err.detail === 'object') {
            // Structured error from backend
            errorMessage = err.detail.message || err.detail.error || 'Validation failed';
            errorDetails = {
              error_type: err.detail.error_type,
              resolution: err.detail.resolution,
              supported_formats: err.detail.supported_formats,
              filename: err.detail.filename
            };
            
            // Show specific toast based on error type
            if (err.detail.error_type === 'FILE_FORMAT_ERROR') {
              toast.error(`Unsupported file format. Use: .csv, .xlsx, or .xls`);
            } else if (err.detail.error_type === 'FILE_PARSE_ERROR') {
              toast.error(err.detail.message || 'Unable to read file. Check encoding and format.');
            } else if (err.detail.error_type === 'EMPTY_FILE') {
              toast.error('File is empty or has no data rows.');
            } else if (err.detail.error_type === 'FILE_SIZE_ERROR') {
              toast.error('File size exceeds limit. Maximum: 50 MB.');
            } else {
              toast.error(errorMessage);
            }
          } else {
            // String error
            errorMessage = err.detail;
            toast.error(errorMessage);
          }
        } else {
          toast.error(errorMessage);
        }
        
        setValidationResult({ 
          error: errorMessage,
          error_details: errorDetails 
        });
      }
    } catch (err) {
      // Network or unexpected error - THIS IS WHERE THE ERROR OCCURS
      debugLog('PREVIEW_CATCH_ERROR', { 
        errorName: err.name,
        errorMessage: err.message,
        errorStack: err.stack,
        errorType: typeof err,
        errorString: String(err),
        requestUrl,
        tokenPresent: !!token
      });
      
      // Show detailed error in UI for debugging
      const debugErrorMsg = `DEBUG: ${err.name}: ${err.message}. URL: ${requestUrl}`;
      console.error('Preview error:', err);
      toast.error(debugErrorMsg);
      setValidationResult({ 
        error: debugErrorMsg,
        debug_info: {
          errorName: err.name,
          errorMessage: err.message,
          requestUrl,
          timestamp: new Date().toISOString()
        }
      });
    } finally {
      setValidating(false);
      setTimeout(() => setProgress(0), 500);
    }
  }, [token, importType]);

  // File drop handler - now properly depends on handlePreview
  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    debugLog('ONDROP_TRIGGERED', {
      acceptedCount: acceptedFiles.length,
      rejectedCount: rejectedFiles.length,
      acceptedFiles: acceptedFiles.map(f => ({ name: f.name, size: f.size, type: f.type })),
      rejectedFiles: rejectedFiles.map(r => ({ 
        file: { name: r.file?.name, size: r.file?.size, type: r.file?.type },
        errors: r.errors 
      }))
    });

    // Handle rejected files with specific error messages
    if (rejectedFiles.length > 0) {
      const rejection = rejectedFiles[0];
      const errors = rejection.errors || [];
      
      debugLog('FILE_REJECTED', { rejection, errors });
      
      // Check specific error codes
      if (errors.some(e => e.code === 'file-too-large')) {
        toast.error('File size exceeds 50 MB limit. Please use a smaller file.');
      } else if (errors.some(e => e.code === 'file-invalid-type')) {
        const filename = rejection.file?.name || '';
        const ext = filename.split('.').pop()?.toLowerCase() || '';
        toast.error(`Unsupported file type: .${ext}. Supported formats: .csv, .xlsx, .xls`);
      } else if (errors.some(e => e.code === 'too-many-files')) {
        toast.error('Please upload only one file at a time.');
      } else {
        // Provide actionable error
        toast.error(`File validation failed: ${errors[0]?.message || 'Unknown error'}. Please check the file format.`);
      }
      return;
    }
    
    if (acceptedFiles.length === 0) {
      debugLog('NO_FILES_ACCEPTED', {});
      return;
    }
    const selectedFile = acceptedFiles[0];
    
    debugLog('FILE_SELECTED', {
      name: selectedFile.name,
      size: selectedFile.size,
      type: selectedFile.type,
      lastModified: selectedFile.lastModified
    });
    
    // Validate file name and extension (allow .xls, .xlsx, .csv)
    const filename = selectedFile.name.toLowerCase();
    const validExtensions = ['.csv', '.xlsx', '.xls'];
    const hasValidExtension = validExtensions.some(ext => filename.endsWith(ext));
    
    if (!hasValidExtension) {
      const ext = filename.split('.').pop() || 'unknown';
      debugLog('INVALID_EXTENSION', { filename, ext });
      toast.error(`Unsupported file format: .${ext}. Please use .csv, .xlsx, or .xls files.`);
      return;
    }
    
    // Check for empty file
    if (selectedFile.size === 0) {
      debugLog('EMPTY_FILE', { filename });
      toast.error('File is empty. Please select a file with data.');
      return;
    }
    
    debugLog('FILE_VALIDATION_PASSED', { filename, size: selectedFile.size });
    
    setFile(selectedFile);
    setPreviewData(null);
    setValidationResult(null);
    setImportResult(null);
    setSchemaCheck(null);
    setIgnoreUnknownColumns(false);
    
    // STATE MACHINE: Reset to VALIDATING
    logStateTransition(importState, 'VALIDATING', { fileName: selectedFile.name });
    setImportState('VALIDATING');
    executingRef.current = false;  // Reset execution lock for new file
    
    // Auto-preview with fresh handlePreview reference
    debugLog('CALLING_HANDLE_PREVIEW', { fileName: selectedFile.name });
    handlePreview(selectedFile);
  }, [handlePreview, importState]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      // Add fallback for when MIME type detection fails
      'application/octet-stream': ['.csv', '.xlsx', '.xls'],
    },
    maxFiles: 1,
    maxSize: MAX_FILE_SIZE,
    disabled: uploading || validating || processing,
    // Don't reject based on MIME type alone - let backend validate
    validator: (file) => {
      const filename = file.name.toLowerCase();
      const validExtensions = ['.csv', '.xlsx', '.xls'];
      if (!validExtensions.some(ext => filename.endsWith(ext))) {
        return {
          code: 'file-invalid-type',
          message: `Unsupported format. Use .csv, .xlsx, or .xls`
        };
      }
      return null;
    }
  });

  // Execute import
  const handleImport = async () => {
    // ==========================================================================
    // P0 FIX: STATE MACHINE GUARDS - Prevent illegal state transitions
    // ==========================================================================
    
    // GUARD 1: Check ref lock (synchronous, survives re-renders)
    if (executingRef.current) {
      logStateTransition(importState, 'BLOCKED', { reason: 'executingRef is locked' });
      return;
    }
    
    // GUARD 2: Check state machine - only allow from VALIDATED or CONFIRMING_SCHEMA
    if (importState === 'EXECUTING' || importState === 'COMPLETED') {
      logStateTransition(importState, 'BLOCKED', { reason: `Invalid state: ${importState}` });
      return;
    }
    
    // GUARD 3: Check processing flag
    if (processing) {
      logStateTransition(importState, 'BLOCKED', { reason: 'Already processing' });
      return;
    }
    
    if (!file || !validationResult?.valid_rows) {
      logStateTransition(importState, 'BLOCKED', { reason: 'No file or valid rows' });
      return;
    }
    
    // GUARD 4: Schema confirmation required
    if (schemaCheck?.has_unknown_columns && !ignoreUnknownColumns) {
      logStateTransition(importState, 'CONFIRMING_SCHEMA', { unknownColumns: schemaCheck.unknown_columns });
      setImportState('CONFIRMING_SCHEMA');
      setShowSchemaWarning(true);
      return;
    }
    
    // ==========================================================================
    // EXECUTE IMPORT - Lock everything immediately
    // ==========================================================================
    executingRef.current = true;  // HARD LOCK
    setImportState('EXECUTING');
    setProcessing(true);
    setShowSchemaWarning(false);  // CLOSE MODAL IMMEDIATELY
    setProgress(0);
    
    logStateTransition('VALIDATED', 'EXECUTING', { fileName: file.name });
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('import_type', importType);
    
    // For brokerage imports, include the user-selected date
    if (importType === 'brokerage') {
      formData.append('brokerage_date', brokerageDate);
    }
    
    // For AUM equity imports with manual date override
    if (importType === 'aum_equity' && showAumDatePicker) {
      formData.append('as_of_date', aumDate);
    }
    
    const queryParams = new URLSearchParams();
    if (ignoreUnknownColumns) {
      queryParams.append('ignore_unknown_columns', 'true');
    }
    
    try {
      const progressInterval = setInterval(() => {
        setProgress((prev) => Math.min(prev + 5, 95));
      }, 300);
      
      const typeConfig = IMPORT_TYPES[importType];
      const url = `${API_URL}${typeConfig.endpoint}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      
      logStateTransition('EXECUTING', 'FETCH_START', { url });
      
      let res;
      try {
        res = await fetch(url, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
      } catch (fetchErr) {
        clearInterval(progressInterval);
        throw new Error(`Network error: ${fetchErr.message}`);
      }
      
      clearInterval(progressInterval);
      setProgress(100);
      
      // Read response as text first, then parse JSON
      // This avoids "body stream already read" and "clone" errors
      let responseText;
      try {
        responseText = await res.text();
      } catch (textErr) {
        console.error('Error reading response text:', textErr);
        // If we can't read the response but status is OK, assume success
        if (res.ok) {
          responseText = JSON.stringify({ 
            success: true, 
            message: 'Import completed (response unreadable)',
            imported: 0
          });
        } else {
          throw new Error(`Failed to read response: ${textErr.message}`);
        }
      }
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseErr) {
        console.error('JSON parse error:', parseErr, 'Raw response:', responseText?.substring(0, 500));
        // If JSON parsing fails but response was OK, import likely succeeded
        if (res.ok) {
          data = { 
            success: true, 
            message: 'Import completed successfully',
            imported: 0  // Unknown count
          };
        } else {
          throw new Error(`Invalid response: ${responseText?.substring(0, 100)}`);
        }
      }
      
      // Handle date_required response for AUM imports
      if (data.status === 'date_required' && data.date_required) {
        setShowAumDatePicker(true);
        setImportState('IDLE');
        setProcessing(false);
        toast.warning('Unable to detect date from file. Please select the As-On date manually.');
        return;
      }
      
      if (res.ok) {
        // SUCCESS - Lock batch ID and transition to COMPLETED
        setCurrentBatchId(data.batch_id);
        setImportResult({
          ...data,
          imported: data.imported || data.success_count || 0
        });
        setImportState('COMPLETED');
        
        const importedCount = data.imported || data.success_count || 0;
        
        logStateTransition('EXECUTING', 'COMPLETED', { 
          batchId: data.batch_id, 
          imported: importedCount,
          alreadyImported: data.already_imported 
        });
        
        // P0: Check for unmatched clients - show modal if any found
        if (data.unmatched_clients && data.unmatched_clients.length > 0) {
          setUnmatchedClients(data.unmatched_clients);
          setShowUnmatchedModal(true);
          toast.warning(`${importedCount} records imported. ${data.unmatched_clients.length} client(s) not found in system.`);
        } else if (data.failed > 0) {
          // Show failed records in toast
          toast.warning(`Imported ${importedCount} records. ${data.failed} record(s) failed - see details below.`);
        } else if (data.already_imported) {
          toast.info(data.message || 'This batch has already been imported successfully.');
        } else if (data.schema_info?.columns_ignored_count > 0) {
          toast.success(`Imported ${importedCount} records. ${data.schema_info.columns_ignored_count} unknown column(s) were ignored.`);
        } else {
          toast.success(`Successfully imported ${importedCount} records`);
        }
        
        // Clear file state to prevent re-import
        setFile(null);
        setPreviewData(null);
        setValidationResult(null);
        setSchemaCheck(null);
        setIgnoreUnknownColumns(false);
        
        fetchHistory();
      } else {
        // FAILURE
        setImportState('FAILED');
        logStateTransition('EXECUTING', 'FAILED', { status: res.status, error: data.detail });
        toast.error(data.detail?.message || data.detail || 'Import failed');
        setImportResult({ error: data.detail });
      }
    } catch (err) {
      setImportState('FAILED');
      logStateTransition('EXECUTING', 'FAILED', { error: err.message });
      
      // Handle response body errors gracefully - import likely succeeded
      const bodyErrors = ['body stream', 'already read', 'already used', 'Response body'];
      const isBodyError = bodyErrors.some(e => err.message?.includes(e));
      
      if (isBodyError) {
        toast.success('Import completed! (Response parsing issue - please verify data)');
        setImportResult({ 
          message: 'Import completed. Please verify data and refresh.', 
          success: true,
          imported: 0
        });
        setImportState('COMPLETED');
      } else {
        toast.error('Import failed: ' + err.message);
        setImportResult({ error: 'Import failed: ' + err.message });
      }
    } finally {
      setProcessing(false);
      executingRef.current = false;  // Release lock only after completion
      setTimeout(() => setProgress(0), 500);
    }
  };

  // Download sample template (P0 UPDATED)
  const downloadSample = (type) => {
    const samples = {
      // P0: Updated Client Master template with required columns
      clients: [
        ['client_code', 'name', 'email', 'phone', 'pan', 'status', 'segment', 'kyc_status', 'address', 'city', 'state', 'assigned_dealer_id', 'assigned_rm_id'],
        ['C00001', 'John Doe', 'john@example.com', '9876543210', 'ABCDE1234F', 'Active', 'retail', 'verified', '123 Main St', 'Mumbai', 'Maharashtra', '', ''],
        ['C00002', 'Jane Smith', 'jane@example.com', '9876543211', 'FGHIJ5678K', 'Active', 'hni', 'pending', '456 Oak Ave', 'Delhi', 'Delhi', '', ''],
        ['C00003', 'Bob Wilson', 'bob@example.com', '9876543212', 'KLMNO9012P', 'Inactive', 'retail', 'verified', '789 Pine Rd', 'Bangalore', 'Karnataka', '', ''],
      ],
      brokerage: [
        ['client_code', 'date', 'equity_turnover', 'equity_brokerage', 'fo_turnover', 'fo_brokerage', 'gross_brokerage', 'net_brokerage'],
        ['C00001', '2025-12-25', '1000000', '500', '5000000', '2500', '3000', '2430'],
        ['C00002', '2025-12-25', '500000', '250', '0', '0', '250', '202.50'],
      ],
      portfolio: [
        ['client_code', 'isin', 'symbol', 'security_name', 'quantity', 'avg_cost', 'current_price', 'market_value', 'valuation_date'],
        ['C00001', 'INE002A01018', 'RELIANCE', 'Reliance Industries', '100', '2400', '2550', '255000', '2025-12-25'],
        ['C00001', 'INE009A01021', 'INFY', 'Infosys Limited', '200', '1500', '1650', '330000', '2025-12-25'],
      ],
      aum: [
        ['scope_code', 'scope_type', 'date', 'total_aum', 'aum_equity', 'aum_mutual_fund', 'aum_derivatives'],
        ['C00001', 'client', '2025-12-25', '2300000', '1500000', '800000', '0'],
        ['C00002', 'client', '2025-12-25', '1800000', '1200000', '600000', '0'],
        ['DLR001', 'dealer', '2025-12-25', '60000000', '45000000', '15000000', '0'],
        ['ISPRAVA', 'firm', '2025-12-25', '150000000', '100000000', '40000000', '10000000'],
      ],
      // P0: AUM Equity template - EXACT column order as per requirement
      aum_equity: [
        ['client_code', 'client_name', 'security_name', 'total_holding', 'close_rate', 'holding_value', 'buy_avg', 'notional_pnl'],
        ['C00001', 'John Doe', 'RELIANCE', '100', '2550.50', '255050', '2400.00', '15050'],
        ['C00001', 'John Doe', 'TCS', '50', '3800.00', '190000', '3500.00', '15000'],
        ['C00001', 'John Doe', 'INFY', '200', '1650.25', '330050', '1700.00', '-9950'],
        ['C00002', 'Jane Smith', 'HDFC BANK', '150', '1580.00', '237000', '1450.00', '19500'],
        ['C00002', 'Jane Smith', 'ICICI BANK', '100', '1120.50', '112050', '1050.00', '7050'],
      ],
    };
    
    const data = samples[type] || samples.clients;
    const csv = data.map((row) => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    
    // Create and trigger download
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `sample_${type}_template.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Cleanup
    setTimeout(() => URL.revokeObjectURL(url), 100);
    toast.success(`Sample ${type} template downloaded`);
  };

  // P0: State for editing unmatched client details
  const [editingClientData, setEditingClientData] = useState({});

  // P0: Handler to update client data before adding
  const handleClientDataChange = (clientCode, field, value) => {
    setEditingClientData(prev => ({
      ...prev,
      [clientCode]: {
        ...(prev[clientCode] || {}),
        [field]: value
      }
    }));
  };

  // P0: Handler to add a new client from unmatched list AND process brokerage
  const handleAddUnmatchedClient = async (unmatchedRecord) => {
    const editedData = editingClientData[unmatchedRecord.client_code] || {};
    const clientName = editedData.name || unmatchedRecord.name;
    
    // Validate that name is provided
    if (!clientName || clientName.trim() === '') {
      toast.error('Please enter a client name before adding');
      return;
    }
    
    setAddingClient(unmatchedRecord.client_code);
    
    try {
      // P0: Use new endpoint that creates client AND processes brokerage together
      const payload = {
        client_code: unmatchedRecord.client_code,
        name: clientName.trim(),
        brokerage_data: unmatchedRecord.data || unmatchedRecord.original_row_data,
        import_type: unmatchedRecord.import_type || 'brokerage'
      };
      
      debugLog('ADDING_UNMATCHED_CLIENT_WITH_BROKERAGE', { payload, original: unmatchedRecord });
      
      const res = await fetch(`${API_URL}/api/clients/create-with-brokerage`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      
      const data = await res.json();
      
      if (res.ok) {
        // Show success message with brokerage info if processed
        if (data.brokerage_processed) {
          toast.success(`Client "${clientName}" added with brokerage ₹${Number(data.brokerage_amount).toLocaleString('en-IN')}`);
        } else {
          toast.success(`Client "${clientName}" added successfully`);
        }
        
        // Remove from unmatched list
        setUnmatchedClients(prev => prev.filter(c => c.client_code !== unmatchedRecord.client_code));
        // Clear editing data for this client
        setEditingClientData(prev => {
          const newData = { ...prev };
          delete newData[unmatchedRecord.client_code];
          return newData;
        });
        // Close modal if all handled
        if (unmatchedClients.length <= 1) {
          setShowUnmatchedModal(false);
          toast.info('All unmatched clients processed. Brokerage data has been added.');
        }
      } else {
        const errorMsg = typeof data.detail === 'string' ? data.detail : data.detail?.message || 'Failed to add client';
        toast.error(errorMsg);
      }
    } catch (err) {
      toast.error(`Error adding client: ${err.message}`);
      debugLog('ADD_CLIENT_ERROR', { error: err.message });
    } finally {
      setAddingClient(null);
    }
  };

  // Reset state
  const handleReset = () => {
    setFile(null);
    setPreviewData(null);
    setValidationResult(null);
    setImportResult(null);
    setProgress(0);
    setShowAumDatePicker(false);  // Reset AUM date picker state
  };

  // Render upload section
  const renderUploadSection = () => {
    const typeConfig = IMPORT_TYPES[importType];
    const TypeIcon = typeConfig.icon;

    return (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Import Type Selection & Upload */}
        <div className="lg:col-span-2 space-y-6">
          {/* Import Type Selection */}
          <Card className="card-dark border-white/5">
            <CardHeader className="pb-4">
              <CardTitle className="text-white">Select Data Type</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {Object.entries(IMPORT_TYPES).map(([key, config]) => {
                  const Icon = config.icon;
                  const isActive = importType === key;
                  return (
                    <button
                      key={key}
                      onClick={() => {
                        setImportType(key);
                        handleReset();
                      }}
                      className={`p-4 rounded-lg border transition-all ${
                        isActive
                          ? 'border-[#C5A059] bg-[#C5A059]/10'
                          : 'border-white/10 hover:border-white/20 hover:bg-white/5'
                      }`}
                    >
                      <Icon className={`w-6 h-6 mx-auto mb-2 ${isActive ? 'text-[#C5A059]' : 'text-slate-400'}`} />
                      <p className={`text-sm font-medium ${isActive ? 'text-white' : 'text-slate-400'}`}>
                        {config.label}
                      </p>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Upload Zone */}
          <Card className="card-dark border-white/5">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <TypeIcon className={`w-5 h-5 ${typeConfig.color}`} />
                Import {typeConfig.label}
              </CardTitle>
              <CardDescription>{typeConfig.description}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Brokerage Date Picker - Required for brokerage imports */}
              {importType === 'brokerage' && (
                <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
                  <div className="flex items-center gap-3 mb-3">
                    <CalendarIcon className="w-5 h-5 text-amber-400" />
                    <div>
                      <p className="text-amber-300 font-medium">Select Brokerage Date</p>
                      <p className="text-amber-200/70 text-sm">This date will be applied to all records in the file</p>
                    </div>
                  </div>
                  <input
                    type="date"
                    value={brokerageDate}
                    onChange={(e) => setBrokerageDate(e.target.value)}
                    className="w-full px-4 py-2 bg-slate-800 border border-amber-500/30 rounded-lg text-white focus:outline-none focus:border-amber-500"
                    data-testid="brokerage-date-picker"
                  />
                  <p className="text-xs text-slate-400 mt-2">
                    Financial Year: April 1 - March 31 | Re-uploading same date replaces previous data
                  </p>
                </div>
              )}
              
              {/* AUM Date Info / Fallback Picker */}
              {importType === 'aum_equity' && (
                <div className={`p-4 rounded-lg ${showAumDatePicker ? 'bg-amber-500/10 border border-amber-500/30' : 'bg-emerald-500/10 border border-emerald-500/30'}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <CalendarIcon className={`w-5 h-5 ${showAumDatePicker ? 'text-amber-400' : 'text-emerald-400'}`} />
                    <div>
                      <p className={`font-medium ${showAumDatePicker ? 'text-amber-300' : 'text-emerald-300'}`}>
                        {showAumDatePicker ? 'Manual Date Selection Required' : 'Auto Date Detection'}
                      </p>
                      <p className={`text-sm ${showAumDatePicker ? 'text-amber-200/70' : 'text-emerald-200/70'}`}>
                        {showAumDatePicker 
                          ? 'Date could not be detected from file. Please select manually.' 
                          : 'Date will be auto-extracted from Row 1 or Row 2 (e.g., "ClientHolding as on 31/12/2025")'}
                      </p>
                    </div>
                  </div>
                  {showAumDatePicker ? (
                    <>
                      <input
                        type="date"
                        value={aumDate}
                        onChange={(e) => setAumDate(e.target.value)}
                        className="w-full px-4 py-2 bg-slate-800 border border-amber-500/30 rounded-lg text-white focus:outline-none focus:border-amber-500"
                        data-testid="aum-date-picker"
                      />
                      <p className="text-xs text-slate-400 mt-2">
                        This date will be used as the AUM snapshot date
                      </p>
                    </>
                  ) : (
                    <div className="text-xs text-slate-400">
                      <p>Supported formats: DD/MM/YYYY, DD-MM-YYYY, YYYY-MM-DD, DD Mon YYYY</p>
                      <p className="mt-1">File structure: Row 1-2 (date header), Row 3-5 (skip), Row 6 (columns), Row 7+ (data)</p>
                    </div>
                  )}
                </div>
              )}
              
              <div
                {...getRootProps()}
                className={`
                  border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all
                  ${isDragActive ? 'border-[#C5A059] bg-[#C5A059]/10' : 'border-white/10 hover:border-white/20 hover:bg-white/5'}
                  ${(uploading || validating || processing) ? 'opacity-50 cursor-not-allowed' : ''}
                `}
              >
                <input {...getInputProps()} />
                {validating || processing ? (
                  <div className="flex flex-col items-center">
                    <Loader2 className="w-12 h-12 text-[#C5A059] animate-spin mb-4" />
                    <p className="text-white">{validating ? 'Validating...' : 'Processing...'}</p>
                    <Progress value={progress} className="w-48 mt-4" />
                  </div>
                ) : file ? (
                  <div className="flex flex-col items-center">
                    <FileSpreadsheet className="w-12 h-12 text-[#C5A059] mb-4" />
                    <p className="text-white font-medium">{file.name}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-sm font-medium ${file.size > MAX_FILE_SIZE ? 'text-red-500' : 'text-emerald-500'}`}>
                        {file.size < 1024 * 1024 
                          ? `${(file.size / 1024).toFixed(1)} KB`
                          : `${(file.size / (1024 * 1024)).toFixed(2)} MB`
                        }
                      </span>
                      <span className="text-slate-500">/ 50 MB max</span>
                    </div>
                    {file.size > MAX_FILE_SIZE && (
                      <p className="text-red-400 text-sm mt-2 flex items-center gap-1">
                        <XCircle className="w-4 h-4" /> File exceeds 50 MB limit
                      </p>
                    )}
                    <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleReset(); }} className="mt-2">
                      <X className="w-4 h-4 mr-1" /> Remove
                    </Button>
                  </div>
                ) : (
                  <>
                    <Upload className="w-12 h-12 text-slate-500 mx-auto mb-4" />
                    {isDragActive ? (
                      <p className="text-white">Drop the file here...</p>
                    ) : (
                      <>
                        <p className="text-white mb-2">Drag & drop your file here, or click to select</p>
                        <p className="text-sm text-slate-500">Supports .csv, .xlsx, .xls (max 50MB)</p>
                      </>
                    )}
                  </>
                )}
              </div>

              {/* Progress Bar */}
              {progress > 0 && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">{validating ? 'Validating' : 'Processing'}...</span>
                    <span className="text-white">{progress}%</span>
                  </div>
                  <Progress value={progress} className="h-2" />
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => downloadSample(importType)}
                  className="flex-1"
                >
                  <Download className="w-4 h-4 mr-2" /> Download Template
                </Button>
                {file && validationResult && !validationResult.error && validationResult.error_count === 0 && (
                  <Button
                    onClick={handleImport}
                    disabled={processing || !validationResult.valid_rows}
                    className="flex-1 bg-[#C5A059] hover:bg-[#b4914e]"
                  >
                    {processing ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4 mr-2" />
                    )}
                    Import {validationResult.valid_rows} Records
                    {validationResult.warning_count > 0 && (
                      <span className="ml-2 text-xs text-amber-200">({validationResult.warning_count} warnings)</span>
                    )}
                  </Button>
                )}
                {file && validationResult && !validationResult.error && validationResult.error_count > 0 && (
                  <Button
                    disabled
                    className="flex-1 bg-slate-700 cursor-not-allowed"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Fix {validationResult.error_count} Errors First
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* P0 DEBUG: Error Details Panel */}
          {validationResult && validationResult.error && (
            <Card className="card-dark border-red-500/50 bg-red-900/20">
              <CardHeader>
                <CardTitle className="text-red-400 flex items-center gap-2">
                  <XCircle className="w-5 h-5" />
                  DEBUG: Validation Error Details
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-4 rounded-lg bg-slate-900 overflow-auto">
                    <p className="text-sm text-red-300 font-mono whitespace-pre-wrap">
                      {validationResult.error}
                    </p>
                  </div>
                  {validationResult.debug_info && (
                    <div className="p-4 rounded-lg bg-slate-900 overflow-auto">
                      <p className="text-xs text-slate-400 mb-2">Debug Info:</p>
                      <pre className="text-xs text-slate-300 font-mono">
                        {JSON.stringify(validationResult.debug_info, null, 2)}
                      </pre>
                    </div>
                  )}
                  {validationResult.error_details && (
                    <div className="p-4 rounded-lg bg-slate-900 overflow-auto">
                      <p className="text-xs text-slate-400 mb-2">Error Details:</p>
                      <pre className="text-xs text-slate-300 font-mono">
                        {JSON.stringify(validationResult.error_details, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Validation Results */}
          {validationResult && !validationResult.error && (
            <Card className="card-dark border-white/5">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Eye className="w-5 h-5 text-[#C5A059]" />
                  Validation Results
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-5 gap-4 mb-6">
                  <div className="p-4 rounded-lg bg-slate-800">
                    <p className="text-2xl font-bold text-white">{validationResult.total_rows}</p>
                    <p className="text-sm text-slate-400">Total Rows</p>
                  </div>
                  <div className="p-4 rounded-lg bg-emerald-500/10">
                    <p className="text-2xl font-bold text-emerald-500">{validationResult.valid_rows}</p>
                    <p className="text-sm text-slate-400">Valid</p>
                  </div>
                  <div className="p-4 rounded-lg bg-red-500/10">
                    <p className="text-2xl font-bold text-red-500">{validationResult.error_count}</p>
                    <p className="text-sm text-slate-400">Errors</p>
                  </div>
                  <div className="p-4 rounded-lg bg-amber-500/10">
                    <p className="text-2xl font-bold text-amber-500">{validationResult.warning_count || 0}</p>
                    <p className="text-sm text-slate-400">Warnings</p>
                  </div>
                  <div className="p-4 rounded-lg bg-blue-500/10">
                    <p className="text-2xl font-bold text-blue-500">{validationResult.duplicate_count || 0}</p>
                    <p className="text-sm text-slate-400">Duplicates</p>
                  </div>
                </div>

                {/* Validation Status */}
                {validationResult.error_count > 0 && (
                  <div className="mb-4 p-4 rounded-lg bg-red-500/10 border border-red-500/30">
                    <div className="flex items-center gap-2 text-red-400">
                      <XCircle className="w-5 h-5" />
                      <span className="font-medium">Cannot proceed: {validationResult.error_count} errors must be fixed</span>
                    </div>
                  </div>
                )}

                {validationResult.error_count === 0 && validationResult.warning_count > 0 && (
                  <div className="mb-4 p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
                    <div className="flex items-center gap-2 text-amber-400">
                      <AlertCircle className="w-5 h-5" />
                      <span className="font-medium">{validationResult.warning_count} warnings found - you can proceed with caution</span>
                    </div>
                  </div>
                )}

                {validationResult.error_count === 0 && !validationResult.warning_count && (
                  <div className="mb-4 p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                    <div className="flex items-center gap-2 text-emerald-400">
                      <CheckCircle2 className="w-5 h-5" />
                      <span className="font-medium">All rows validated successfully - ready to import</span>
                    </div>
                  </div>
                )}

                {/* CRITICAL: Schema Warning Banner */}
                {schemaCheck?.has_unknown_columns && (
                  <div className="mb-4 p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="font-medium text-amber-400 mb-2">
                          Schema Warning: {schemaCheck.unknown_columns.length} Unknown Column(s) Detected
                        </p>
                        <div className="flex flex-wrap gap-2 mb-3">
                          {schemaCheck.unknown_columns.map((col, idx) => (
                            <span key={idx} className="px-2 py-1 bg-amber-500/20 text-amber-300 text-xs rounded font-mono">
                              {col}
                            </span>
                          ))}
                        </div>
                        <p className="text-sm text-slate-400">
                          These columns are NOT part of the schema and will be <strong className="text-amber-400">IGNORED</strong>.
                          {' '}You will need to confirm before importing.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Error List */}
                {validationResult.errors?.length > 0 && (
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm font-medium text-red-400 flex items-center gap-2">
                        <XCircle className="w-4 h-4" /> Errors (Block Import)
                      </p>
                      <Button variant="ghost" size="sm" onClick={() => setShowErrorDialog(true)}>
                        View All <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {validationResult.errors.slice(0, 5).map((err, idx) => (
                        <div key={idx} className="flex items-start gap-3 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                          <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                          <div className="text-sm">
                            <span className="text-red-400 font-mono">Row {err.row_number}</span>
                            <span className="text-slate-400 mx-2">|</span>
                            <span className="text-slate-300">{err.field}: </span>
                            <span className="text-red-400">{err.message}</span>
                            {err.resolution && (
                              <p className="text-xs text-slate-500 mt-1">💡 {err.resolution}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Resolution Guidance Summary */}
                    {validationResult.resolution_guidance && (
                      <div className="mt-4 p-3 rounded-lg bg-slate-800 border border-white/10">
                        <p className="text-sm text-slate-300 flex items-center gap-2">
                          <Info className="w-4 h-4 text-blue-400" />
                          {validationResult.resolution_guidance.message}
                        </p>
                        {validationResult.resolution_guidance.safe_to_retry && (
                          <p className="text-xs text-slate-500 mt-1">
                            ✓ Safe to re-upload after fixing the source file
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Warning List */}
                {validationResult.warnings?.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm font-medium text-amber-400 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" /> Warnings (Allow with Override)
                      </p>
                    </div>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {validationResult.warnings.slice(0, 5).map((warn, idx) => (
                        <div key={idx} className="flex items-start gap-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                          <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                          <div className="text-sm">
                            <span className="text-amber-400 font-mono">Row {warn.row_number}</span>
                            <span className="text-slate-400 mx-2">|</span>
                            <span className="text-slate-300">{warn.field}: </span>
                            <span className="text-amber-400">{warn.message}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Import Result */}
          {importResult && !importResult.error && (
            <Card className="card-dark border-emerald-500/30">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                  Import Complete
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className={`grid ${importResult.failed > 0 ? 'grid-cols-4' : 'grid-cols-3'} gap-4`}>
                  <div className="p-4 rounded-lg bg-emerald-500/10">
                    <p className="text-2xl font-bold text-emerald-500">{importResult.imported || importResult.success_count || 0}</p>
                    <p className="text-sm text-slate-400">Imported</p>
                  </div>
                  <div className="p-4 rounded-lg bg-blue-500/10">
                    <p className="text-2xl font-bold text-blue-500">{importResult.new_records || importResult.imported || importResult.success_count || 0}</p>
                    <p className="text-sm text-slate-400">New Records</p>
                  </div>
                  <div className="p-4 rounded-lg bg-amber-500/10">
                    <p className="text-2xl font-bold text-amber-500">{importResult.updated_records || 0}</p>
                    <p className="text-sm text-slate-400">Updated</p>
                  </div>
                  {importResult.failed > 0 && (
                    <div className="p-4 rounded-lg bg-red-500/10">
                      <p className="text-2xl font-bold text-red-500">{importResult.failed}</p>
                      <p className="text-sm text-slate-400">Failed</p>
                    </div>
                  )}
                </div>
                
                {/* Show failed records count and details */}
                {importResult.failed > 0 && (
                  <div className="mt-4 p-4 rounded-lg bg-red-500/10 border border-red-500/20">
                    <p className="text-sm font-semibold text-red-400 mb-2 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      {importResult.failed} Record(s) Failed
                    </p>
                    
                    {/* Show error details */}
                    {importResult.errors && importResult.errors.length > 0 && (
                      <div className="space-y-2 mt-3">
                        <p className="text-xs text-slate-400 mb-2">Failed records details:</p>
                        <div className="max-h-48 overflow-y-auto space-y-2">
                          {importResult.errors.slice(0, 10).map((err, idx) => (
                            <div key={idx} className="p-2 rounded bg-slate-800/50 border border-red-500/10">
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1">
                                  <p className="text-xs text-red-400 font-medium">
                                    Row {err.row_number}: {err.message || err.error_code || 'Processing error'}
                                  </p>
                                  {err.row_data && (
                                    <div className="mt-1 text-xs text-slate-500">
                                      {/* Show client code if available */}
                                      {(err.row_data.account_id || err.row_data.accountid || err.row_data.client_code || err.row_data.clientcode) && (
                                        <span className="mr-3">
                                          Client: <span className="text-slate-400">{err.row_data.account_id || err.row_data.accountid || err.row_data.client_code || err.row_data.clientcode}</span>
                                        </span>
                                      )}
                                      {/* Show account name if available */}
                                      {(err.row_data.account_name || err.row_data.accountname) && (
                                        <span className="mr-3">
                                          Name: <span className="text-slate-400">{err.row_data.account_name || err.row_data.accountname}</span>
                                        </span>
                                      )}
                                      {/* Show debit/credit for ledger balance */}
                                      {(err.row_data.net_debit || err.row_data.netdebit) && (
                                        <span className="mr-3">
                                          Debit: <span className="text-slate-400">{err.row_data.net_debit || err.row_data.netdebit}</span>
                                        </span>
                                      )}
                                      {(err.row_data.net_credit || err.row_data.netcredit) && (
                                        <span>
                                          Credit: <span className="text-slate-400">{err.row_data.net_credit || err.row_data.netcredit}</span>
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                          {importResult.errors.length > 10 && (
                            <p className="text-xs text-slate-500 text-center py-1">
                              ... and {importResult.errors.length - 10} more errors
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Show unmatched clients separately if any */}
                    {importResult.unmatched_clients && importResult.unmatched_clients.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-red-500/20">
                        <p className="text-xs text-amber-400 mb-2">
                          {importResult.unmatched_clients.length} client(s) not found in Client Master
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {importResult.unmatched_clients.slice(0, 5).map((uc, idx) => (
                            <span key={idx} className="px-2 py-0.5 bg-amber-500/10 text-amber-400 text-xs rounded">
                              {uc.client_code}
                            </span>
                          ))}
                          {importResult.unmatched_clients.length > 5 && (
                            <span className="px-2 py-0.5 bg-slate-700 text-slate-400 text-xs rounded">
                              +{importResult.unmatched_clients.length - 5} more
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Show aggregation results for brokerage imports */}
                {importResult.aggregation && importResult.aggregation.aggregation_completed && (
                  <div className="mt-4 p-4 rounded-lg bg-violet-500/10 border border-violet-500/20">
                    <p className="text-sm font-semibold text-violet-400 mb-2 flex items-center gap-2">
                      <RefreshCcw className="w-4 h-4" />
                      Dashboard Aggregation Complete
                    </p>
                    <div className="grid grid-cols-2 gap-2 text-xs text-slate-400">
                      <div>Clients Updated: <span className="text-white">{importResult.aggregation.clients_updated}</span></div>
                      <div>Dealers Updated: <span className="text-white">{importResult.aggregation.dealers_updated}</span></div>
                      <div>RMs Updated: <span className="text-white">{importResult.aggregation.rms_updated}</span></div>
                      <div>Monthly Periods: <span className="text-white">{importResult.aggregation.monthly_periods}</span></div>
                      <div>Latest Brokerage: <span className="text-white">{importResult.aggregation.latest_brokerage_date}</span></div>
                      <div>Total Value: <span className="text-emerald-400">₹{(importResult.aggregation.total_brokerage || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span></div>
                    </div>
                  </div>
                )}
                
                <Button onClick={handleReset} variant="outline" className="w-full mt-4">
                  <RefreshCcw className="w-4 h-4 mr-2" /> Import Another File
                </Button>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right: Instructions */}
        <div className="space-y-6">
          <Card className="card-dark border-white/5">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                Required Columns
                {typeConfig.columnHelp && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <HelpCircle className="w-4 h-4 text-slate-400" />
                      </TooltipTrigger>
                      <TooltipContent className="bg-slate-800 border-slate-700 max-w-xs">
                        <p className="text-sm">Hover over each column for detailed explanation</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {typeConfig.requiredColumns.map((col) => {
                const helpInfo = typeConfig.columnHelp?.[col];
                
                return (
                  <div key={col} className="flex items-center gap-3 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                    <div className="flex-1">
                      <code className="text-sm font-mono text-white">{col}</code>
                      {helpInfo && (
                        <p className="text-xs text-slate-400 mt-0.5">{helpInfo.label}</p>
                      )}
                    </div>
                    {helpInfo && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <Info className="w-4 h-4 text-slate-400 hover:text-[#C5A059] cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent side="left" className="bg-slate-800 border-slate-700 max-w-sm p-4">
                            <div className="space-y-2">
                              <p className="font-medium text-white">{helpInfo.label}</p>
                              <p className="text-sm text-slate-300">{helpInfo.description}</p>
                              {helpInfo.values && (
                                <div className="mt-2 space-y-1">
                                  <p className="text-xs text-slate-400 font-medium">Allowed Values:</p>
                                  {helpInfo.values.map((v, i) => (
                                    <div key={i} className="flex items-start gap-2 text-xs">
                                      <code className="px-1 py-0.5 bg-slate-700 rounded text-[#C5A059]">{v.value}</code>
                                      <span className="text-slate-400">{v.desc}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                              {helpInfo.example && (
                                <div className="mt-2 pt-2 border-t border-slate-700">
                                  <p className="text-xs text-slate-400">
                                    <span className="font-medium">Example:</span> {helpInfo.example}
                                  </p>
                                </div>
                              )}
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>

          <Card className="card-dark border-white/5">
            <CardHeader>
              <CardTitle className="text-white">Optional Columns</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {typeConfig.optionalColumns.map((col) => (
                  <Badge key={col} variant="outline" className="font-mono text-xs">
                    {col}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Supported File Formats - P0 */}
          <Card className="card-dark border-white/5">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-base flex items-center gap-2">
                <FileSpreadsheet className="w-4 h-4 text-[#C5A059]" />
                Supported File Formats
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-2">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-emerald-500" />
                  <span className="text-slate-300">.xlsx</span>
                  <span className="text-slate-500 text-xs">Excel 2007+</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-emerald-500" />
                  <span className="text-slate-300">.xls</span>
                  <span className="text-slate-500 text-xs">Excel 97-2003</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-emerald-500" />
                  <span className="text-slate-300">.csv</span>
                  <span className="text-slate-500 text-xs">UTF-8 encoded</span>
                </div>
              </div>
              <div className="pt-2 border-t border-white/5 text-xs text-slate-500">
                <p>CSV files must use comma (,) delimiter and UTF-8 encoding.</p>
              </div>
            </CardContent>
          </Card>

          {/* AUM-specific explanation card */}
          {importType === 'aum' && (
            <Card className="card-dark border-[#C5A059]/30 bg-[#C5A059]/5">
              <CardHeader className="pb-2">
                <CardTitle className="text-[#C5A059] text-base flex items-center gap-2">
                  <Database className="w-4 h-4" />
                  Understanding AUM Records
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-3">
                <div>
                  <p className="text-white font-medium mb-1">What is scope_type?</p>
                  <p className="text-slate-400">Defines the entity level for the AUM record:</p>
                  <ul className="mt-1 space-y-1 text-slate-300">
                    <li>• <code className="text-[#C5A059]">client</code> - Individual client AUM</li>
                    <li>• <code className="text-[#C5A059]">dealer</code> - RM&apos;s total client AUM</li>
                    <li>• <code className="text-[#C5A059]">firm</code> - Entire firm AUM</li>
                  </ul>
                </div>
                <div>
                  <p className="text-white font-medium mb-1">What is scope_code?</p>
                  <p className="text-slate-400">The unique identifier matching the scope_type:</p>
                  <ul className="mt-1 space-y-1 text-slate-300">
                    <li>• Client Code (e.g., <code className="text-[#C5A059]">C00001</code>)</li>
                    <li>• Dealer Code (e.g., <code className="text-[#C5A059]">DLR001</code>)</li>
                    <li>• Firm ID (e.g., <code className="text-[#C5A059]">ISPRAVA</code>)</li>
                  </ul>
                </div>
                <div className="pt-2 border-t border-white/10">
                  <p className="text-xs text-slate-500">
                    💡 Tip: You can import AUM at multiple levels in the same file
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="card-dark border-amber-500/20 bg-amber-500/5">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-500 mt-0.5" />
                <div className="text-sm text-amber-200">
                  <p className="font-medium mb-1">Import Guidelines</p>
                  <ul className="space-y-1 text-amber-200/80">
                    <li>• Max file size: <strong>50 MB</strong></li>
                    <li>• Formats: <strong>.xlsx</strong>, <strong>.xls</strong>, or <strong>.csv</strong></li>
                    <li>• First row must be column headers</li>
                    <li>• CSV must be UTF-8 encoded, comma delimiter</li>
                    <li>• Numbers: No currency symbols</li>
                    <li>• Duplicates will update existing records</li>
                    <li>• Re-uploads are versioned (no data loss)</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-dark border-blue-500/20 bg-blue-500/5">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <Database className="w-5 h-5 text-blue-500 mt-0.5" />
                <div className="text-sm text-blue-200">
                  <p className="font-medium mb-1">Compliance & Audit</p>
                  <ul className="space-y-1 text-blue-200/80">
                    <li>• All uploads are audit-logged</li>
                    <li>• History retained for 24 months</li>
                    <li>• SEBI compliant data handling</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  };

  // Render history section
  const renderHistorySection = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-white">Import History</h2>
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <Clock className="w-4 h-4" />
          <span>Retention: 24 months</span>
        </div>
        <Button variant="outline" onClick={fetchHistory}>
          <RefreshCcw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {importHistory.length === 0 ? (
        <Card className="card-dark border-white/5">
          <CardContent className="py-10 text-center">
            <History className="w-12 h-12 mx-auto text-slate-500 mb-4" />
            <p className="text-slate-400">No import history found.</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="card-dark border-white/5">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="border-white/5">
                  <TableHead className="text-slate-400">Batch</TableHead>
                  <TableHead className="text-slate-400">Type</TableHead>
                  <TableHead className="text-slate-400">File</TableHead>
                  <TableHead className="text-slate-400">Status</TableHead>
                  <TableHead className="text-slate-400">Records</TableHead>
                  <TableHead className="text-slate-400">Uploaded By</TableHead>
                  <TableHead className="text-slate-400">Date</TableHead>
                  <TableHead className="text-slate-400"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {importHistory.map((batch) => (
                  <TableRow key={batch.id} className="border-white/5 hover:bg-white/5">
                    <TableCell className="font-mono text-sm text-white">
                      {batch.batch_number}
                      {batch.version > 1 && (
                        <span className="ml-1 text-xs text-amber-500">v{batch.version}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-slate-300 capitalize">{batch.import_type?.replace('_', ' ')}</TableCell>
                    <TableCell className="text-slate-300">
                      <div className="flex flex-col">
                        <span className="truncate max-w-[150px]">{batch.file_name}</span>
                        <span className="text-xs text-slate-500">
                          {(batch.file_size_bytes / 1024).toFixed(1)} KB
                        </span>
                      </div>
                    </TableCell>
                    <TableCell><StatusBadge status={batch.status} /></TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span>
                          <span className="text-emerald-500">{batch.successful_rows || batch.imported || 0}</span>
                          <span className="text-slate-500"> / {batch.total_rows}</span>
                        </span>
                        {(batch.new_records > 0 || batch.updated_records > 0) && (
                          <span className="text-xs text-slate-500">
                            {batch.new_records > 0 && <span className="text-blue-400">{batch.new_records} new</span>}
                            {batch.new_records > 0 && batch.updated_records > 0 && ', '}
                            {batch.updated_records > 0 && <span className="text-amber-400">{batch.updated_records} updated</span>}
                          </span>
                        )}
                        {batch.failed_rows > 0 && (
                          <span className="text-xs text-red-500">({batch.failed_rows} errors)</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-400 text-sm">{batch.uploaded_by_email}</TableCell>
                    <TableCell className="text-slate-400 text-sm">
                      {new Date(batch.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      {batch.errors?.length > 0 && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setSelectedBatch(batch);
                            setShowErrorDialog(true);
                          }}
                        >
                          <FileWarning className="w-4 h-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );

  // Render preview section
  const renderPreviewSection = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-white">Data Preview</h2>
      
      {!previewData ? (
        <Card className="card-dark border-white/5">
          <CardContent className="py-10 text-center">
            <Eye className="w-12 h-12 mx-auto text-slate-500 mb-4" />
            <p className="text-slate-400">Upload a file to preview data.</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="card-dark border-white/5">
          <CardHeader>
            <CardTitle className="text-white">First 10 Rows Preview</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-white/5">
                  {previewData.columns?.map((col) => (
                    <TableHead key={col} className="text-slate-400 font-mono text-xs">{col}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {previewData.rows?.map((row, idx) => (
                  <TableRow key={idx} className="border-white/5">
                    {previewData.columns?.map((col) => (
                      <TableCell key={col} className="text-slate-300 text-sm">{row[col] || '-'}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );

  // Rollback data type options - NOTE: Client Master is intentionally NOT included for safety
  const ROLLBACK_DATA_TYPES = [
    { value: 'brokerage', label: 'Brokerage Data', icon: Wallet },
    { value: 'brokerage_daily', label: 'Brokerage Daily', icon: Wallet },
    { value: 'portfolio', label: 'Portfolio Holdings (AUM)', icon: BarChart3 },
    { value: 'aum', label: 'AUM Records', icon: Database },
    { value: 'aum_equity', label: 'AUM Equity', icon: TrendingUp },
    { value: 'ledger_balance', label: 'Ledger Balance', icon: Wallet },
  ];

  // Render rollback section
  const renderRollbackSection = () => (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-red-500/20">
          <RefreshCcw className="w-6 h-6 text-red-500" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-white">Data Rollback</h2>
          <p className="text-slate-400 text-sm">Delete records after a specified date to restore previous state</p>
        </div>
      </div>

      <Card className="card-dark border-red-500/20">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-500" />
            Rollback Configuration
          </CardTitle>
          <CardDescription className="text-slate-400">
            Select data type and date. Records dated AFTER the selected date will be permanently deleted.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Data Type Selection */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-300">Select Data Type</label>
            <Select
              value={rollbackDataType}
              onValueChange={(value) => {
                setRollbackDataType(value);
                setRollbackPreview(null);
                setRollbackDate('');
                fetchRollbackDateRanges(value);
              }}
            >
              <SelectTrigger className="bg-slate-800 border-white/10 text-white">
                <SelectValue placeholder="Select data type" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-white/10">
                {ROLLBACK_DATA_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value} className="text-white hover:bg-white/10">
                    <div className="flex items-center gap-2">
                      <type.icon className="w-4 h-4" />
                      {type.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Date Selection */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-300">Rollback to Date (keep records up to this date)</label>
            <div className="flex gap-3">
              <input
                type="date"
                value={rollbackDate}
                onChange={(e) => {
                  setRollbackDate(e.target.value);
                  setRollbackPreview(null);
                }}
                className="flex-1 px-3 py-2 rounded-md bg-slate-800 border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-red-500"
              />
              <Button
                onClick={handleRollbackPreview}
                disabled={!rollbackDate || rollbackLoading}
                variant="outline"
                className="border-red-500/50 text-red-400 hover:bg-red-500/10"
              >
                {rollbackLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Eye className="w-4 h-4 mr-2" />
                )}
                Preview
              </Button>
            </div>
          </div>

          {/* Available Dates Info */}
          {rollbackDateRanges && (
            <div className="p-4 rounded-lg bg-slate-800/50 border border-white/5">
              <h4 className="text-sm font-medium text-slate-300 mb-3">Available Dates in {ROLLBACK_DATA_TYPES.find(t => t.value === rollbackDataType)?.label}</h4>
              <div className="text-sm text-slate-400 mb-2">
                Total Records: <span className="text-white font-medium">{rollbackDateRanges.total_records?.toLocaleString()}</span>
              </div>
              <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                {rollbackDateRanges.available_dates?.slice(0, 20).map((d) => (
                  <Badge
                    key={d.date}
                    variant="outline"
                    className="cursor-pointer hover:bg-white/10 text-slate-300 border-white/20"
                    onClick={() => setRollbackDate(d.date)}
                  >
                    {d.date} ({d.record_count})
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Preview Results */}
          {rollbackPreview && (
            <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30 space-y-4">
              <h4 className="text-lg font-semibold text-red-400 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                Rollback Preview
              </h4>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 rounded bg-slate-800/50">
                  <div className="text-sm text-slate-400">Records to DELETE</div>
                  <div className="text-2xl font-bold text-red-500">{rollbackPreview.records_to_delete?.toLocaleString()}</div>
                  <div className="text-xs text-slate-500">After {rollbackPreview.rollback_date}</div>
                </div>
                <div className="p-3 rounded bg-slate-800/50">
                  <div className="text-sm text-slate-400">Records to KEEP</div>
                  <div className="text-2xl font-bold text-emerald-500">{rollbackPreview.records_to_keep?.toLocaleString()}</div>
                  <div className="text-xs text-slate-500">On or before {rollbackPreview.rollback_date}</div>
                </div>
              </div>

              {rollbackPreview.sample_dates_to_delete?.length > 0 && (
                <div>
                  <div className="text-sm text-slate-400 mb-2">Dates that will be deleted:</div>
                  <div className="flex flex-wrap gap-2">
                    {rollbackPreview.sample_dates_to_delete.map((date) => (
                      <Badge key={date} className="bg-red-500/20 text-red-400 border-red-500/30">
                        {date}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              <div className="pt-4 border-t border-red-500/20">
                <Button
                  onClick={() => setShowRollbackConfirm(true)}
                  disabled={rollbackPreview.records_to_delete === 0}
                  className="w-full bg-red-600 hover:bg-red-700 text-white"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Delete {rollbackPreview.records_to_delete?.toLocaleString()} Records
                </Button>
              </div>
            </div>
          )}

          {/* Recompute Aggregates Section */}
          <div className="mt-6 p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-amber-400 font-medium flex items-center gap-2">
                  <RefreshCcw className="w-4 h-4" />
                  Refresh Dashboard Data
                </h4>
                <p className="text-sm text-slate-400 mt-1">
                  Click to recalculate all brokerage aggregates and update dashboard revenue
                </p>
              </div>
              <Button
                onClick={handleRecomputeAggregates}
                disabled={recomputingAggregates}
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                {recomputingAggregates ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCcw className="w-4 h-4 mr-2" />
                )}
                Recompute Now
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Rollback Confirmation Dialog */}
      <Dialog open={showRollbackConfirm} onOpenChange={setShowRollbackConfirm}>
        <DialogContent className="bg-slate-900 border-red-500/30">
          <DialogHeader>
            <DialogTitle className="text-red-500 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              Confirm Data Rollback
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              This action cannot be undone. The following records will be permanently deleted:
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-slate-400">Data Type:</span>
              <span className="text-white font-medium">{rollbackPreview?.display_name}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-400">Rollback Date:</span>
              <span className="text-white font-medium">{rollbackPreview?.rollback_date}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-400">Records to Delete:</span>
              <span className="text-red-500 font-bold">{rollbackPreview?.records_to_delete?.toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-400">Records to Keep:</span>
              <span className="text-emerald-500 font-medium">{rollbackPreview?.records_to_keep?.toLocaleString()}</span>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowRollbackConfirm(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleRollbackExecute}
              disabled={rollbackLoading}
              className="bg-red-600 hover:bg-red-700"
            >
              {rollbackLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <XCircle className="w-4 h-4 mr-2" />
              )}
              Confirm Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <Upload className="w-7 h-7 text-[#C5A059]" />
          Data Import
        </h1>
        <p className="text-slate-400 mt-1">
          Import client data, transactions, and brokerage from Excel or CSV files
        </p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-800">
          <TabsTrigger value="upload" className="data-[state=active]:bg-[#C5A059]">
            <Upload className="w-4 h-4 mr-2" /> Upload
          </TabsTrigger>
          <TabsTrigger value="preview" className="data-[state=active]:bg-[#C5A059]">
            <Eye className="w-4 h-4 mr-2" /> Preview
          </TabsTrigger>
          <TabsTrigger value="history" className="data-[state=active]:bg-[#C5A059]">
            <History className="w-4 h-4 mr-2" /> History
          </TabsTrigger>
          <TabsTrigger value="rollback" className="data-[state=active]:bg-red-600">
            <RefreshCcw className="w-4 h-4 mr-2" /> Rollback
          </TabsTrigger>
        </TabsList>

        <TabsContent value="upload" className="mt-6">
          {renderUploadSection()}
        </TabsContent>

        <TabsContent value="preview" className="mt-6">
          {renderPreviewSection()}
        </TabsContent>

        <TabsContent value="history" className="mt-6">
          {renderHistorySection()}
        </TabsContent>

        <TabsContent value="rollback" className="mt-6">
          {renderRollbackSection()}
        </TabsContent>
      </Tabs>

      {/* Error Dialog */}
      <Dialog open={showErrorDialog} onOpenChange={setShowErrorDialog}>
        <DialogContent className="bg-slate-900 border-white/10 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Validation Errors</DialogTitle>
            <DialogDescription>
              {selectedBatch ? `Batch: ${selectedBatch.batch_number}` : 'File validation errors'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {(selectedBatch?.errors || validationResult?.errors || []).map((err, idx) => (
              <div key={idx} className="flex items-start gap-3 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <span className="text-red-400 font-mono">Row {err.row_number || err.row}</span>
                  {err.field && (
                    <>
                      <span className="text-slate-400 mx-2">|</span>
                      <span className="text-slate-300">Column: {err.field}</span>
                    </>
                  )}
                  {err.value && (
                    <>
                      <span className="text-slate-400 mx-2">|</span>
                      <span className="text-slate-300">Value: &quot;{err.value}&quot;</span>
                    </>
                  )}
                  <p className="text-red-400 mt-1">{err.message || err.error_code}</p>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowErrorDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CRITICAL: Schema Warning Dialog */}
      <Dialog open={showSchemaWarning} onOpenChange={setShowSchemaWarning}>
        <DialogContent className="bg-slate-900 border-white/10 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-amber-500 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              Schema Validation Warning
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Your file contains columns that are NOT part of the predefined schema.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Unknown Columns List */}
            <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg">
              <div className="text-amber-400 font-medium mb-2">
                Unknown Columns ({schemaCheck?.unknown_columns?.length || 0}):
              </div>
              <div className="flex flex-wrap gap-2">
                {schemaCheck?.unknown_columns?.map((col, idx) => (
                  <span key={idx} className="px-2 py-1 bg-amber-500/20 text-amber-300 text-sm rounded">
                    {col}
                  </span>
                ))}
              </div>
            </div>

            {/* Valid Schema Fields */}
            <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
              <div className="text-emerald-400 font-medium mb-2">
                Valid Schema Fields:
              </div>
              <div className="flex flex-wrap gap-2">
                {schemaCheck?.valid_schema_fields?.map((field, idx) => (
                  <span key={idx} className="px-2 py-1 bg-emerald-500/20 text-emerald-300 text-xs rounded">
                    {field}
                  </span>
                ))}
              </div>
            </div>

            {/* Warning Message */}
            <div className="p-4 bg-slate-800 border border-white/10 rounded-lg">
              <div className="text-white font-medium mb-2">Important:</div>
              <ul className="text-sm text-slate-400 space-y-1 list-disc list-inside">
                <li>Unknown columns will be <strong className="text-amber-400">IGNORED</strong> during import</li>
                <li>No new fields will be created in the database</li>
                <li>Only data from valid schema columns will be imported</li>
              </ul>
            </div>
          </div>

          <DialogFooter className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                logStateTransition(importState, 'IDLE', { action: 'User cancelled schema confirmation' });
                setImportState('IDLE');
                setShowSchemaWarning(false);
                setFile(null);
                setPreviewData(null);
                setValidationResult(null);
                setSchemaCheck(null);
                setIgnoreUnknownColumns(false);
              }}
              disabled={importState === 'EXECUTING'}
              className="border-white/10"
            >
              <X className="w-4 h-4 mr-2" />
              Cancel & Correct File
            </Button>
            <Button 
              onClick={() => {
                // STATE MACHINE: Only proceed if not already executing
                if (importState === 'EXECUTING' || executingRef.current) {
                  logStateTransition(importState, 'BLOCKED', { reason: 'Already executing from dialog' });
                  return;
                }
                
                logStateTransition(importState, 'CONFIRMED_SCHEMA', { action: 'User confirmed ignore columns' });
                
                // Set confirmation flag BEFORE calling handleImport
                setIgnoreUnknownColumns(true);
                
                // Close modal IMMEDIATELY to prevent re-triggering
                setShowSchemaWarning(false);
                
                // Call handleImport directly (no setTimeout - synchronous flow)
                // handleImport will check guards and proceed
                setTimeout(() => handleImport(), 0);
              }}
              disabled={importState === 'EXECUTING' || processing}
              className="bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {processing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Ignore Extra Columns & Proceed
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* P0: Unmatched Clients Modal */}
      <Dialog open={showUnmatchedModal} onOpenChange={setShowUnmatchedModal}>
        <DialogContent className="bg-slate-900 border-amber-500/30 max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-amber-500 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              Unmatched Client Codes ({unmatchedClients.length})
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              The following client codes were found in the import file but do not exist in the system.
              You can add them as new clients to include their data in future imports.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700">
                  <TableHead className="text-slate-400">Client Code</TableHead>
                  <TableHead className="text-slate-400">Client Name *</TableHead>
                  <TableHead className="text-slate-400">Import Data</TableHead>
                  <TableHead className="text-slate-400 text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {unmatchedClients.map((client, idx) => (
                  <TableRow key={idx} className="border-slate-700/50">
                    <TableCell className="font-mono text-amber-400 font-medium">
                      {client.client_code || 'Unknown'}
                    </TableCell>
                    <TableCell>
                      <Input
                        type="text"
                        placeholder="Enter client name (required)"
                        value={editingClientData[client.client_code]?.name || client.name || ''}
                        onChange={(e) => handleClientDataChange(client.client_code, 'name', e.target.value)}
                        className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500 w-48"
                        data-testid={`client-name-input-${client.client_code}`}
                      />
                    </TableCell>
                    <TableCell className="text-slate-400 text-sm max-w-xs">
                      {/* Brokerage data */}
                      {client.data?.brokerage && (
                        <span className="block">Brokerage: ₹{Number(client.data.brokerage).toLocaleString('en-IN')}</span>
                      )}
                      {/* AUM Equity data */}
                      {client.data?.aum && (
                        <span className="block">AUM: ₹{Number(client.data.aum).toLocaleString('en-IN')}</span>
                      )}
                      {client.data?.security_name && (
                        <span className="block">Security: {client.data.security_name}</span>
                      )}
                      {/* Ledger Balance data */}
                      {client.data?.net_debit && Number(client.data.net_debit) > 0 && (
                        <span className="block text-red-400">Debit: ₹{Number(client.data.net_debit).toLocaleString('en-IN')}</span>
                      )}
                      {client.data?.net_credit && Number(client.data.net_credit) > 0 && (
                        <span className="block text-emerald-400">Credit: ₹{Number(client.data.net_credit).toLocaleString('en-IN')}</span>
                      )}
                      {/* Date */}
                      {client.data?.trade_date && (
                        <span className="block text-slate-500">Date: {client.data.trade_date}</span>
                      )}
                      {/* Show import type */}
                      {client.import_type && (
                        <Badge variant="outline" className="mt-1 text-xs">{client.import_type}</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        onClick={() => handleAddUnmatchedClient(client)}
                        disabled={addingClient === client.client_code}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid={`add-client-${client.client_code}`}
                      >
                        {addingClient === client.client_code ? (
                          <>
                            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                            Adding...
                          </>
                        ) : (
                          <>
                            <Users className="w-3 h-3 mr-1" />
                            Add Client
                          </>
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            
            <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700">
              <p className="text-sm text-slate-400">
                <Info className="w-4 h-4 inline mr-1" />
                After adding clients, re-import the file to include their transaction data.
              </p>
            </div>
          </div>
          
          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => setShowUnmatchedModal(false)}
              className="border-slate-600 text-slate-300"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ImportPage;
